<script setup lang="ts">
import { nextTick, reactive, ref } from 'vue';
import type { SavedConnectionInput } from '../model.js';
import AppIcon from './AppIcon.vue';

const props = defineProps<{ busy: boolean; error: string; groups: readonly string[] }>();
const emit = defineEmits<{ save: [input: SavedConnectionInput]; close: [] }>();

const dialog = ref<HTMLDialogElement>();
const nameInput = ref<HTMLInputElement>();
const defaults = () => ({
  name: '',
  group: 'Personal',
  sshAlias: '',
  remoteProgram: 'atum-remote-access.exe',
});
const profile = reactive(defaults());

async function open() {
  Object.assign(profile, defaults());
  dialog.value?.showModal();
  await nextTick();
  nameInput.value?.focus();
}
function close() {
  dialog.value?.close();
}
defineExpose({ open, close });
</script>

<template>
  <dialog ref="dialog" class="dialog" aria-labelledby="add-device-title" @close="emit('close')">
    <form class="dialog-body" @submit.prevent="emit('save', { ...profile })">
      <header class="dialog-head">
        <div>
          <p class="eyebrow">New device</p>
          <h2 id="add-device-title">Add a device</h2>
        </div>
        <button
          type="button"
          class="icon-button quiet"
          aria-label="Close add device"
          :disabled="props.busy"
          @click="close"
        >
          <AppIcon name="close" />
        </button>
      </header>
      <p>
        Use an SSH connection already configured on this computer. The app saves the alias, never a
        key.
      </p>
      <div v-if="props.error" role="alert" class="banner error">
        <AppIcon name="warning" /><span>{{ props.error }}</span>
      </div>
      <label class="field">
        <span class="field-label">Device name</span>
        <input
          ref="nameInput"
          v-model="profile.name"
          required
          maxlength="80"
          placeholder="e.g. VULCAN"
          autocomplete="off"
        />
      </label>
      <label class="field">
        <span class="field-label">SSH alias</span>
        <input
          v-model="profile.sshAlias"
          class="mono"
          required
          maxlength="128"
          pattern="[A-Za-z0-9][A-Za-z0-9._-]*"
          placeholder="e.g. vulcan"
          spellcheck="false"
          autocomplete="off"
        />
        <span class="field-help">
          A Host entry from your SSH configuration, including its relay, key and pinned host
          identity.
        </span>
      </label>
      <label class="field">
        <span class="field-label">Group</span>
        <input
          v-model="profile.group"
          list="add-device-groups"
          required
          maxlength="60"
          autocomplete="off"
        />
        <datalist id="add-device-groups">
          <option v-for="group in props.groups" :key="group" :value="group" />
        </datalist>
        <span class="field-help">
          A place, a company or a client. Groups organize the list and never grant access.
        </span>
      </label>
      <details class="disclosure">
        <summary>Advanced connection settings</summary>
        <label class="field">
          <span class="field-label">Remote program</span>
          <input
            v-model="profile.remoteProgram"
            class="mono"
            required
            maxlength="1024"
            spellcheck="false"
            autocomplete="off"
          />
          <span class="field-help">The owner access executable installed on the device.</span>
        </label>
      </details>
      <p class="hint">Device availability is checked when you connect, not when you save.</p>
      <footer class="dialog-actions">
        <button type="button" :disabled="props.busy" @click="close">Cancel</button>
        <button type="submit" class="primary" :disabled="props.busy">
          {{ props.busy ? 'Saving…' : 'Save device' }}
        </button>
      </footer>
    </form>
  </dialog>
</template>
